import { apiClient } from './apiClient';

// Antes esto era un solo
// `POST /api/upload` con FormData, buffer-izado completo en RAM del backend antes de subir a
// GCS — sin límite de tamaño, cualquier sesión válida podía tumbar el proceso con un archivo
// grande. Ahora hay dos caminos: `profile`/`img-avatar` (necesitan resize server-side con
// `sharp`, se quedan en el flujo viejo vía `/api/upload/avatar`, con límite real de multer) y
// todo lo demás (sube directo a GCS con una signed POST policy — el archivo nunca toca el
// backend, GCS mismo valida tamaño/tipo).
const AVATAR_TYPES = new Set(['profile', 'img-avatar', 'space_icon']);

// Endpoint genérico de subida de archivos (usado por varias features: avatars, logos de
// espacio, adjuntos de comentarios, etc.) — se centraliza acá para no repetir el armado de
// FormData en cada feature que lo necesite.
// `extraFields` cubre casos como los frames de avatar, que además del archivo mandan
// `userId` en el form (dato, no identidad — la sesión viaja en la cookie vía apiClient), o
// los assets de decoración (`name`/`category`/`width`/`height`/`tags`).
// `returnFull: true` devuelve la respuesta completa (incluye `path`, y para `decoration` la
// fila de `decoration`/`media`) en vez de solo `url` — para los pocos consumidores que
// necesitan más que la URL (ej. `ClientsView.jsx` guarda `path`/`type` en `documents[]`).
export async function uploadFile(file, type = 'image', { extraFields = {}, onProgress, returnFull = false } = {}) {
    if (AVATAR_TYPES.has(type)) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        Object.entries(extraFields).forEach(([key, value]) => formData.append(key, value));
        const data = await apiClient.post('/api/upload/avatar', formData);
        if (!data.success || !data.url) throw new Error(data.error || 'Upload failed');
        return returnFull ? data : data.url;
    }

    const { url, fields, path } = await apiClient.post('/api/upload/request', {
        type,
        filename: file.name,
        contentType: file.type,
    });

    const gcsForm = new FormData();
    Object.entries(fields).forEach(([key, value]) => gcsForm.append(key, value));
    gcsForm.append('file', file); // último campo — requisito de la POST policy de GCS

    await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        if (onProgress) {
            xhr.upload.onprogress = (ev) => {
                if (ev.lengthComputable) onProgress(Math.round((ev.loaded / ev.total) * 100));
            };
        }
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload to GCS failed')));
        xhr.onerror = () => reject(new Error('Upload to GCS failed'));
        xhr.open('POST', url);
        xhr.send(gcsForm);
    });

    const data = await apiClient.post('/api/upload/confirm', {
        path,
        type,
        name: file.name,
        size: file.size,
        contentType: file.type,
        ...extraFields,
    });
    if (!data.success || !data.url) throw new Error(data.error || 'Upload failed');
    return returnFull ? data : data.url;
}
